import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import Heading from 'shopper/components/Heading';

import useQueryClient from 'hooks/useQueryClient';
import useToast from 'hooks/useToast';

import placeholder from 'lib/placeholder';

import SwitchGroup from './SwitchGroup';

const Settings = ({
  className,
  mutationFn,
  queryFn,
  queryKey,
  settings,
  skeleton: Skeleton,
  subscribedKey,
  title,
  unsubscribedKey,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: ({ signal }) => queryFn({ signal }),
    select: (permissions) => permissions[subscribedKey],
    cacheTime: 0,
  });

  const { mutate: mutateTogglePermission } = useMutation({
    mutationFn,
    onSettled: (_, __, { action, permission }) => {
      queryClient.setQueryData(queryKey, (stored) => {
        if (action === 'subscribe') {
          return {
            [subscribedKey]: [...stored[subscribedKey], permission],
            [unsubscribedKey]: stored[unsubscribedKey].filter(
              (p) => p !== permission
            ),
          };
        }

        return {
          [subscribedKey]: stored[subscribedKey].filter(
            (p) => p !== permission
          ),
          [unsubscribedKey]: [...stored[subscribedKey], permission],
        };
      });
    },
    onError: (_, { action, permission }) => {
      queryClient.setQueryData(queryKey, (stored) => {
        if (action === 'subscribe') {
          return {
            [subscribedKey]: stored[unsubscribedKey].filter(
              (p) => p !== permission
            ),
            [unsubscribedKey]: [...stored[subscribedKey], permission],
          };
        }

        return {
          [subscribedKey]: [...stored[subscribedKey], permission],
          [unsubscribedKey]: stored[unsubscribedKey].filter(
            (p) => p !== permission
          ),
        };
      });
      showToast({
        type: 'danger',
        text: placeholder('toasts.errors.unknownShort'),
      });
    },
  });

  const onToggle = (event) => {
    const permission = event.target.name;
    const isChecked = event.target.checked;

    mutateTogglePermission({
      action: isChecked ? 'subscribe' : 'unsubscribe',
      permission,
    });
  };

  return (
    <section className={className}>
      <Heading
        as="h2"
        className="text-neutral-low-100 dark:text-neutral-high-200"
        size="size3"
      >
        {title}
      </Heading>
      <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-7 md:grid-cols-2">
        {isLoading ? (
          <Skeleton />
        ) : (
          settings.map((setting) => (
            <SwitchGroup
              key={setting.title}
              switches={setting.options}
              title={setting.title}
              verifyChecked={(id) => data.includes(id)}
              onToggle={onToggle}
            />
          ))
        )}
      </div>
    </section>
  );
};

Settings.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  mutationFn: PropTypes.func.isRequired,
  queryFn: PropTypes.func.isRequired,
  queryKey: PropTypes.array.isRequired,
  settings: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  skeleton: PropTypes.func.isRequired,
  subscribedKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  unsubscribedKey: PropTypes.string.isRequired,
};

export default Settings;
